import { QueryParameters } from '@deck.gl/carto'
import { call, put, select } from 'redux-saga/effects'
import { Source } from '@carto/react-redux'
import { shallowEqual } from 'react-redux'

import { RootState } from 'app/state/store'
import { RequestStatus } from 'features/common/types'
import { Connection, getConnectionByNameAndProvider } from 'features/connections'
import { Formats, Map, MapDataset, MapType } from 'features/maps/types'
import { createMapDataset, patchDataset } from 'features/maps/api/maps'
import { getDatasetById, updateCurrentMapDataset } from 'features/maps/state/mapsSlice'

import { BuilderMode, LayerRequest } from 'features/builder/types'
import { isDatasetLoadingTiles } from './keplerGlSlice'
import { updateTilesetDatasetResolution, updateTilesetDatasetTilejson } from './keplerGlActions'
import { getLayerLoader } from './builderSagas'
import { getTilejsonFromTilesetDataset } from './builderSliceUtils'
import { showGlobalBuilderError } from './builderSlice'
import { getTilesetLayerClasses } from './keplerGlSlice'
import { getDatasetColumnsFromMap, getNewDatasetColor } from './datasetUtils'
import { runTransactionSaga } from 'features/common/utils/transactions'
import { waitUntilSelectorEqual } from 'features/common/state/sagaUtils'
import { isAgentDataset } from '../model/agent'

export function* saveNewDatasetConfig({
  type,
  source,
  format,
  geoColumn,
  columns,
  label,
  color,
  provider,
  connection,
  queryTemplate,
  queryParameters
}: {
  type: LayerRequest['type']
  source: LayerRequest['source']
  format: LayerRequest['format']
  geoColumn: LayerRequest['geoColumn']
  columns?: string[] | null
  label: string
  color: string
  provider?: LayerRequest['provider']
  connection?: LayerRequest['connection']
  queryTemplate?: LayerRequest['queryTemplate']
  queryParameters?: LayerRequest['queryParameters']
}) {
  try {
    const currentMap: Map = yield select((state) => state.maps.currentMap)
    const mapId = currentMap.id
    let connectionId = ''
    if (provider && connection) {
      const connectionData: Connection = yield select(getConnectionByNameAndProvider, { connection, provider })
      connectionId = connectionData.id as string
    }
    const mapDataset: MapDataset = yield call(createMapDataset, mapId, {
      type,
      source,
      format,
      geoColumn,
      connectionId,
      columns,
      label,
      color,
      queryTemplate,
      queryParameters
    } as MapDataset)

    // POST /datasets doesn't return connectionName
    if (!mapDataset.connectionName) {
      mapDataset.connectionName = connection
    }
    return mapDataset
  } catch (error) {
    throw new Error(error as string)
  }
}

interface UpdateDatasetParams {
  source?: string
  format?: Formats
  geoColumn?: string | null
  aggregationExp?: string | null
  aggregationResLevel?: number | null
  queryParameters?: QueryParameters | null
  queryTemplate?: string | null
  columns?: string[] | null
}

export function* refreshDataset({
  datasetId,
  noCache = true,
  propsToUpdate
}: {
  datasetId: string
  noCache?: boolean
  propsToUpdate?: Partial<MapDataset>
}) {
  // eslint-disable-next-line redux-saga/no-unhandled-errors
  const dataset: MapDataset = yield select(getDatasetById, datasetId)
  // eslint-disable-next-line redux-saga/no-unhandled-errors
  const currentMap: Map = yield select((state) => state.maps.currentMap)
  // eslint-disable-next-line redux-saga/no-unhandled-errors
  const cartoDataSources: Record<string, Source> = yield select((state) => state.carto.dataSources)

  const datasetRequest = {
    ...dataset,
    mapId: currentMap.id,
    datasetId,
    provider: dataset.providerId,
    connection: dataset.connectionName,
    datasetLabel: dataset.label,
    datasetColor: dataset.color,
    filters: cartoDataSources[datasetId]?.filters,
    ...propsToUpdate
  }
  yield getLayerLoader(datasetRequest).refreshDataset({ noCache })
}

export function* updateDataset(datasetId: string, datasetPropsUpdate: UpdateDatasetParams) {
  let oldDataset
  let rollbackProps = false
  try {
    const currentMap: Map = yield select((state) => state.maps.currentMap)
    if (!currentMap) return

    oldDataset = yield select((state) => state.maps.currentMapDatasets.find((d) => d.id === datasetId))
    if (!oldDataset) return

    const needUpdate = Object.entries(datasetPropsUpdate).some(([key, value]) => !shallowEqual(oldDataset[key], value))

    if (!needUpdate) {
      return
    }

    yield put(
      updateCurrentMapDataset({
        id: datasetId,
        dataset: datasetPropsUpdate
      })
    )
    rollbackProps = true

    const dataset = yield select((state) => state.maps.currentMapDatasets.find((d) => d.id === datasetId))
    const builderMode: BuilderMode = yield select((state) => state.builder.builderMode)
    const cartoDataSources: Record<string, Source> = yield select((state) => state.carto.dataSources)

    const loader = getLayerLoader({
      ...dataset,
      datasetId: datasetId,
      mapId: currentMap.id,
      provider: dataset.providerId,
      connection: dataset.connectionName,
      filters: cartoDataSources[datasetId]?.filters
    })
    if (!loader) throw new Error('updateDataset: No loader found')

    const keplerDataset = yield loader.getKeplerDataset()
    if (!keplerDataset) {
      // hail our error handling, getKeplerDataset should setup dataset in error state already
      return
    }

    if (builderMode === BuilderMode.Editor && !isAgentDataset(datasetId)) {
      yield call(patchDataset, {
        mapId: currentMap.id,
        datasetId: datasetId,
        body: datasetPropsUpdate
      })
    }

    const tilejson = getTilejsonFromTilesetDataset(keplerDataset)

    if (oldDataset.aggregationResLevel !== datasetPropsUpdate.aggregationResLevel) {
      // Update dataset props
      yield put(
        updateTilesetDatasetResolution({
          datasetId,
          aggregationResLevel: dataset.aggregationResLevel
        })
      )
    }
    yield put(updateTilesetDatasetTilejson({ datasetId, tilejson }))
    updateCurrentMapDataset({
      id: datasetId,
      dataset: {
        status: RequestStatus.Fulfilled,
        errorMessage: undefined
      }
    })
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    console.error('updateDataset', error)
    yield put(
      updateCurrentMapDataset({
        id: datasetId,
        dataset: {
          ...(rollbackProps && oldDataset ? oldDataset : {}),
          status: RequestStatus.Rejected,
          errorMessage: (error as Error).message
        }
      })
    )

    const msg = `Error updating the dataset. ${error.message}`
    yield put(showGlobalBuilderError(msg))
  }
}

export function* updateDatasetColumnsFromMap(datasetId: string) {
  try {
    const builderMode: BuilderMode = yield select((state) => state.builder.builderMode)
    if (builderMode !== BuilderMode.Editor) {
      return
    }

    yield runDatasetTransaction(datasetId, function* updateDatasetColumnsFromMap() {
      const dataset: MapDataset = yield select((state) => getDatasetById(state, datasetId))
      if (!dataset || dataset.status === RequestStatus.Rejected || dataset.status === RequestStatus.Pending) {
        return
      }

      const columns = yield getDatasetColumnsFromMap(datasetId)

      yield updateDataset(datasetId, { columns })
    })

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (e: any) {
    console.error('updateDatasetColumnsFromState', e)
    const msg = `Error updating columns. ${e.message}`
    yield put(showGlobalBuilderError(msg))
  }
}

export function* updateDatasetsColumns() {
  try {
    const datasets = yield select((state: RootState) => state.maps.currentMapDatasets)

    for (const dataset of datasets) {
      yield updateDatasetColumnsFromMap(dataset.id)
    }
  } catch (error) {
    console.error('updateDatasetsColumns', error)
  }
}

export function* getNextDatasetColor() {
  try {
    const currentMapDatasets: MapDataset[] = yield select((state) => state.maps.currentMapDatasets)
    const newColor = getNewDatasetColor(currentMapDatasets)
    return newColor
  } catch (error) {
    throw new Error(error as string)
  }
}

export function* getDatasetSupportedLayerClasses({ type, format }: { type: MapType; format?: Formats }) {
  if (type !== MapType.table || format === Formats.TILEJSON || format === Formats.RASTER) {
    return getTilesetLayerClasses()
  } else {
    // eslint-disable-next-line redux-saga/no-unhandled-errors
    return yield select((state) => state.keplerGl.map.visState.layerClasses)
  }
}

export function* waitAllTilesLoaded(datasetId: string) {
  yield waitUntilSelectorEqual((state: RootState) => isDatasetLoadingTiles(state, datasetId), false)
}

export function runDatasetTransaction(datasetId: string, saga: unknown, ...args: unknown[]) {
  return runTransactionSaga(`builder-dataset-${datasetId}`, saga, ...args)
}
